import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Chess } from 'chess.js';
import type { Color, GameResult, PromotionPiece, PublicMove } from '../../shared/protocol.ts';
import { botByLevel, chooseBotMove, HINT_SEARCH, type BotLevel } from '../lib/bots.ts';
import { engine, parseUci, SearchCancelledError } from '../lib/engine.ts';
import type { MoveLike } from '../lib/chess.ts';
import { sound, soundForMove } from '../lib/sound.ts';
import { readStorage, removeStorage, writeStorage } from '../lib/storage.ts';

export interface ComputerGameConfig {
  level: BotLevel;
  color: Color;
}

interface SavedGame extends ComputerGameConfig {
  moves: PublicMove[];
  result: GameResult | null;
}

const SAVE_KEY = 'outchess:computer-game';

export function loadSavedComputerGame(): SavedGame | null {
  const saved = readStorage<SavedGame | null>(SAVE_KEY, null);
  if (!saved || !Array.isArray(saved.moves) || (saved.color !== 'w' && saved.color !== 'b')) return null;
  return saved;
}

export function saveComputerGame(game: SavedGame) {
  writeStorage(SAVE_KEY, game);
}

export function clearComputerGame() {
  removeStorage(SAVE_KEY);
}

function resultOf(chess: Chess): GameResult | null {
  if (chess.isCheckmate()) return { winner: chess.turn() === 'w' ? 'b' : 'w', reason: 'checkmate' };
  if (chess.isStalemate()) return { winner: null, reason: 'stalemate' };
  if (chess.isInsufficientMaterial()) return { winner: null, reason: 'insufficient_material' };
  if (chess.isThreefoldRepetition()) return { winner: null, reason: 'threefold_repetition' };
  if (chess.isDrawByFiftyMoves()) return { winner: null, reason: 'fifty_move_rule' };
  return null;
}

function replay(moves: readonly PublicMove[]): Chess {
  const chess = new Chess();
  for (const m of moves) chess.move({ from: m.from, to: m.to, promotion: m.promotion });
  return chess;
}

export function useComputerGame(initial: SavedGame) {
  const [config, setConfig] = useState<ComputerGameConfig>({ level: initial.level, color: initial.color });
  const [moves, setMoves] = useState<PublicMove[]>(initial.moves);
  const [result, setResult] = useState<GameResult | null>(initial.result);
  const [gameId, setGameId] = useState(1);
  const [hint, setHint] = useState<{ from: string; to: string } | null>(null);
  const [engineError, setEngineError] = useState(false);

  const chess = useMemo(() => replay(moves), [moves]);
  const fen = chess.fen();
  const bot = botByLevel(config.level);
  const botColor: Color = config.color === 'w' ? 'b' : 'w';
  const isBotTurn = result === null && chess.turn() === botColor;
  // The engine is working whenever it is the computer's move.
  const thinking = isBotTurn;

  // Persist after every change so a refresh resumes the game.
  useEffect(() => {
    saveComputerGame({ ...config, moves, result });
  }, [config, moves, result]);

  useEffect(() => engine.warmUp(), []);

  // Latest moves for callbacks that run outside render (engine replies).
  // Updated together with every setMoves call below.
  const movesRef = useRef(moves);

  const commit = useCallback((move: MoveLike) => {
    const next = replay(movesRef.current);
    let made;
    try {
      made = next.move({ from: move.from, to: move.to, promotion: move.promotion });
    } catch {
      sound.play('illegal');
      return;
    }
    const record: PublicMove = {
      san: made.san,
      from: made.from,
      to: made.to,
      ...(made.promotion ? { promotion: made.promotion as PromotionPiece } : {}),
    };
    const updated = [...movesRef.current, record];
    movesRef.current = updated;
    setMoves(updated);
    setHint(null);
    sound.play(soundForMove(made));

    const outcome = resultOf(next);
    if (outcome) {
      setResult(outcome); // the result screen plays the outcome sound
    }
  }, []);

  // Computer's turn: ask the engine, then play its (possibly humanised) move.
  useEffect(() => {
    if (!isBotTurn) return;
    let cancelled = false;
    const startedAt = performance.now();

    engine
      .search({ fen, ...bot.search })
      .then(async ({ bestMove, lines }) => {
        const uci = chooseBotMove(bot, fen, bestMove, lines);
        // Weak engine settings answer instantly; a short pause feels more natural.
        const minDelay = 350 + Math.random() * 350;
        const elapsed = performance.now() - startedAt;
        if (elapsed < minDelay) await new Promise((r) => setTimeout(r, minDelay - elapsed));
        if (!cancelled) commit(parseUci(uci));
      })
      .catch((err) => {
        if (cancelled || err instanceof SearchCancelledError) return;
        // Engine unavailable: fall back to a random legal move so the game continues.
        setEngineError(true);
        const legal = new Chess(fen).moves({ verbose: true });
        const pick = legal[Math.floor(Math.random() * legal.length)];
        if (pick) commit({ from: pick.from, to: pick.to, promotion: pick.promotion as PromotionPiece | undefined });
      });

    return () => {
      cancelled = true;
      engine.cancel();
    };
  }, [isBotTurn, fen, bot, commit]);

  const playerMove = useCallback(
    (move: MoveLike) => {
      if (result || chess.turn() !== config.color) return;
      commit(move);
    },
    [result, chess, config.color, commit],
  );

  /** Take back to the player's previous turn (one or two plies). */
  const undo = useCallback(() => {
    const prev = movesRef.current;
    // When the player is Black the computer's opening move always stays.
    const floor = config.color === 'b' ? 1 : 0;
    if (prev.length <= floor) return;
    const lastMover: Color = prev.length % 2 === 1 ? 'w' : 'b';
    const length = Math.max(floor, prev.length - (lastMover === config.color ? 1 : 2));
    const updated = prev.slice(0, length);
    movesRef.current = updated;
    setMoves(updated);
    setResult(null);
    setHint(null);
  }, [config.color]);

  const resign = useCallback(() => {
    if (result) return;
    setResult({ winner: botColor, reason: 'resignation' });
  }, [result, botColor]);

  const requestHint = useCallback(() => {
    if (result || chess.turn() !== config.color || thinking) return;
    engine
      .search({ fen, ...HINT_SEARCH })
      .then(({ bestMove }) => {
        const { from, to } = parseUci(bestMove);
        setHint({ from, to });
      })
      .catch(() => undefined);
  }, [result, chess, config.color, thinking, fen]);

  const restart = useCallback((next?: Partial<ComputerGameConfig>) => {
    engine.newGame();
    setConfig((prev) => ({ ...prev, ...next }));
    movesRef.current = [];
    setMoves([]);
    setGameId((id) => id + 1);
    setResult(null);
    setHint(null);
    sound.play('start');
  }, []);

  const canUndo = !thinking && moves.length > (config.color === 'b' ? 1 : 0);

  return {
    config,
    gameId,
    bot,
    moves,
    fen,
    chess,
    result,
    thinking,
    hint,
    engineError,
    canUndo,
    playerMove,
    undo,
    resign,
    requestHint,
    restart,
  };
}
